import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, Filter, Download, Calendar, User, Activity, AlertCircle, RefreshCw, ChevronLeft, ChevronRight, Building2, Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUpgrade } from '../../contexts/UpgradeContext';

interface AuditLog {
  _id: string;
  accountId: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  isImpersonated: boolean;
  originalAdminId?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  originalAdminEmail?: string;
  action: string;
  resource: string;
  resourceId?: string;
  description: string;
  method?: string;
  endpoint?: string;
  ip?: string;
  userAgent?: string;
  changes?: Array<{
    field: string;
    oldValue?: any;
    newValue?: any;
  }>;
  metadata?: Record<string, any>;
  timestamp: string;
}

interface AuditStats {
  totalLogs: number;
  impersonationCount: number;
  actionStats: Array<{ action: string; count: number }>;
  resourceStats: Array<{ resource: string; count: number }>;
  topUsers: Array<{
    userId: string;
    name: string;
    email: string;
    count: number;
  }>;
  period: {
    startDate: string;
    endDate: string;
  };
}

interface UserOption {
  _id: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface AccountOption {
  _id: string;
  label: string;
  companyName: string;
}

export const AuditLogs: React.FC = () => {
  const { user } = useAuth();
  const { openUpgradeModal } = useUpgrade();
  const isSystemAdmin = user?.role === 'System Administrator';
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [retentionDays, setRetentionDays] = useState<number>(180);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>('premium');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  // Filters
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 7 days
    endDate: new Date().toISOString().split('T')[0],
    accountId: '', // For System Admins
    userId: '',
    action: '',
    resource: '',
    isImpersonated: '',
    search: ''
  });

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');

      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        ...(filters.startDate && { startDate: new Date(filters.startDate).toISOString() }),
        ...(filters.endDate && { endDate: new Date(filters.endDate + 'T23:59:59').toISOString() }),
        ...(filters.accountId && { accountId: filters.accountId }),
        ...(filters.userId && { userId: filters.userId }),
        ...(filters.action && { action: filters.action }),
        ...(filters.resource && { resource: filters.resource }),
        ...(filters.isImpersonated && { isImpersonated: filters.isImpersonated }),
        ...(filters.search && { search: filters.search })
      });

      const response = await fetch(`${apiUrl}/api/audit-logs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const data = await response.json();
      
      if (data.success) {
        setLogs(data.data.logs || []);
        
        // Handle different response structures for free vs premium
        if (data.data.pagination) {
          setTotalPages(data.data.pagination.totalPages);
          setTotal(data.data.pagination.total);
        } else {
          // Free tier response structure
          setTotalPages(data.data.totalPages || 1);
          setTotal(data.data.totalCount || 0);
        }
        
        if (data.data.retentionDays !== undefined) {
          setRetentionDays(data.data.retentionDays);
        }
        if (data.data.subscriptionPlan) {
          setSubscriptionPlan(data.data.subscriptionPlan);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch audit logs');
      }
    } catch (err) {
      console.error('Fetch logs error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');

      const queryParams = new URLSearchParams({
        ...(filters.startDate && { startDate: new Date(filters.startDate).toISOString() }),
        ...(filters.endDate && { endDate: new Date(filters.endDate + 'T23:59:59').toISOString() }),
        ...(filters.accountId && { accountId: filters.accountId })
      });

      const response = await fetch(`${apiUrl}/api/audit-logs/stats?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.data);
        }
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  }, [filters.startDate, filters.endDate]);

  const fetchUserOptions = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');

      const queryParams = new URLSearchParams({
        ...(filters.accountId && { accountId: filters.accountId })
      });

      const response = await fetch(`${apiUrl}/api/audit-logs/users?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUserOptions(data.data);
        }
      }
    } catch (err) {
      console.error('Fetch users error:', err);
    }
  }, [filters.accountId]);

  const fetchAccountOptions = useCallback(async () => {
    if (!isSystemAdmin) return;
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/audit-logs/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAccountOptions(data.data);
        }
      }
    } catch (err) {
      console.error('Fetch accounts error:', err);
    }
  }, [isSystemAdmin]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchUserOptions();
  }, [fetchUserOptions]);

  useEffect(() => {
    fetchAccountOptions();
  }, [fetchAccountOptions]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleReset = () => {
    setFilters({
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      accountId: '',
      userId: '',
      action: '',
      resource: '',
      isImpersonated: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date);
  };

  const getActionColor = (action: string) => {
    const colors: Record<string, string> = {
      view: 'bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300',
      create: 'bg-success-100 dark:bg-success-800 text-success-700 dark:text-success-300',
      update: 'bg-warning-100 dark:bg-warning-800 text-warning-700 dark:text-warning-300',
      delete: 'bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300',
      approve: 'bg-success-100 dark:bg-success-800 text-success-700 dark:text-success-300',
      reject: 'bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-300',
      login: 'bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300',
      logout: 'bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300',
      impersonate_start: 'bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300',
      impersonate_stop: 'bg-pink-100 dark:bg-pink-800 text-pink-700 dark:text-pink-300',
      export: 'bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300',
      import: 'bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300',
      send: 'bg-success-100 dark:bg-success-800 text-success-700 dark:text-success-300',
      download: 'bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300',
      upload: 'bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300'
    };
    return colors[action] || 'bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Audit Logs</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track all user activities and system changes
          </p>
        </div>
        <button
          onClick={() => fetchLogs()}
          className="px-4 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Retention Notice */}
      {subscriptionPlan === 'free' ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-700 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-700 dark:text-yellow-400">Audit Logs Not Available on Free Plan</h3>
              <p className="text-sm text-yellow-900 dark:text-yellow-100 mt-1">
                Upgrade to Premium to access audit logs with 6 months retention and full activity tracking.
              </p>
              <button
                onClick={() => openUpgradeModal({ returnPath: '/admin/audit' })}
                className="mt-3 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition flex items-center gap-2"
              >
                <Zap className="w-4 h-4" />
                Upgrade to Premium
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700/50 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-700 dark:text-blue-400" />
            <p className="text-sm text-blue-900 dark:text-blue-100">
              Showing audit logs from the last <span className="font-semibold">{retentionDays} days</span>
              {subscriptionPlan === 'system_admin' && ' (System Admin - All Accounts)'}
            </p>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent-100 dark:bg-accent-800 rounded-lg">
                <FileText className="w-5 h-5 text-accent-700 dark:text-accent-300" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalLogs}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Events</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-800 rounded-lg">
                <User className="w-5 h-5 text-purple-700 dark:text-purple-300" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.impersonationCount}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Impersonations</div>
              </div>
            </div>
          </div>

          {stats.actionStats.slice(0, 3).map((stat, idx) => (
            <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-success-100 dark:bg-success-800 rounded-lg">
                  <Activity className="w-5 h-5 text-success-700 dark:text-success-300" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat.count}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">{stat.action}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filters</h3>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Reset
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-500"
            >
              {showFilters ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {isSystemAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Account
                </label>
                <select
                  value={filters.accountId}
                  onChange={(e) => handleFilterChange('accountId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-600 dark:text-gray-100"
                >
                  <option value="">All Accounts</option>
                  {accountOptions.map(account => (
                    <option key={account._id} value={account._id}>{account.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-600 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-600 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <User className="w-4 h-4 inline mr-1" />
                User
              </label>
              <select
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-600 dark:text-gray-100"
              >
                <option value="">All Users</option>
                {userOptions.map(user => (
                  <option key={user._id} value={user._id}>{user.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Activity className="w-4 h-4 inline mr-1" />
                Action
              </label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-600 dark:text-gray-100"
              >
                <option value="">All Actions</option>
                <option value="view">View</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="approve">Approve</option>
                <option value="reject">Reject</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="impersonate_start">Impersonate Start</option>
                <option value="impersonate_stop">Impersonate Stop</option>
                <option value="export">Export</option>
                <option value="import">Import</option>
                <option value="send">Send</option>
                <option value="download">Download</option>
                <option value="upload">Upload</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Resource
              </label>
              <select
                value={filters.resource}
                onChange={(e) => handleFilterChange('resource', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-600 dark:text-gray-100"
              >
                <option value="">All Resources</option>
                <option value="Projects">Projects</option>
                <option value="Staff">Staff</option>
                <option value="Calendar">Calendar</option>
                <option value="Timesheets">Timesheets</option>
                <option value="Buddy AI">Buddy AI</option>
                <option value="Leave">Leave</option>
                <option value="Contacts">Contacts</option>
                <option value="Admin">Admin</option>
                <option value="Dashboard">Dashboard</option>
                <option value="Invoices">Invoices</option>
                <option value="Bills">Bills</option>
                <option value="Quotes">Quotes</option>
                <option value="Tasks">Tasks</option>
                <option value="Materials">Materials</option>
                <option value="Services">Services</option>
                <option value="Equipment">Equipment</option>
                <option value="Users">Users</option>
                <option value="Roles">Roles</option>
                <option value="Account">Account</option>
                <option value="Integrations">Integrations</option>
                <option value="Settings">Settings</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Impersonated
              </label>
              <select
                value={filters.isImpersonated}
                onChange={(e) => handleFilterChange('isImpersonated', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-600 dark:text-gray-100"
              >
                <option value="">All</option>
                <option value="true">Impersonated Only</option>
                <option value="false">Not Impersonated</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Search className="w-4 h-4 inline mr-1" />
                Search Description
              </label>
              <input
                type="text"
                placeholder="Search in descriptions..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-600 dark:text-gray-100"
              />
            </div>
          </div>
        )}
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-primary-500" />
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading audit logs...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12 text-error-600 dark:text-error-400">
            <AlertCircle className="w-6 h-6 mr-2" />
            {error}
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <FileText className="w-12 h-12 mb-3 opacity-50" />
            <p>No audit logs found for the selected filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Timestamp
                    </th>
                    {isSystemAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Account
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      IP Address
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  {logs.map((log) => (
                    <tr key={log._id} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(log.timestamp)}
                      </td>
                      {isSystemAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {(log.accountId as any)?.companyName || 'N/A'}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {log.userId?.firstName} {log.userId?.lastName}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{log.userId?.email}</div>
                            {log.isImpersonated && log.originalAdminEmail && (
                              <div className="text-xs text-purple-600 dark:text-purple-400 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                By: {log.originalAdminEmail}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionColor(log.action)}`}>
                          {log.action.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {log.resource}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-md truncate">
                        {log.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {log.ip || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-500">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * limit, total)}</span> of{' '}
                  <span className="font-medium">{total}</span> results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-1 text-gray-700 dark:text-gray-300">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

